// PhotoLibrary — modal for browsing, uploading, tagging, and deleting
// the customer's saved listing photos. Per spec §15.5.
//
// Two visible modes (props.mode):
//   - 'manage'  — full CRUD; used from the Brand Kit "Photo library" tile
//   - 'pick'    — picker that calls onPick(photo) when a tile is clicked,
//                 used from the editor or future "insert photo" flows
//
// Photos are private (signed URLs, 1h TTL). Re-fetching the list re-issues
// signed URLs.

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  X,
  Upload,
  Trash2,
  Search,
  Tag as TagIcon,
  ImageIcon,
  Pencil,
  Check,
} from 'lucide-react';
import {
  deletePhoto,
  listPhotos,
  patchPhoto,
  uploadPhoto,
  StudioApiError,
} from '../lib/api';
import type { SavedPhoto } from '../lib/types';
import { qk } from '../lib/queryClient';
import { useStudio } from '../lib/store';
import { ConfirmDialog } from './ConfirmDialog';

const MAX_BYTES = 25 * 1024 * 1024;

interface PhotoLibraryProps {
  open: boolean;
  onClose: () => void;
  mode?: 'manage' | 'pick';
  onPick?: (photo: SavedPhoto) => void;
}

export function PhotoLibrary({ open, onClose, mode = 'manage', onPick }: PhotoLibraryProps) {
  const qc = useQueryClient();
  const pushToast = useStudio((s) => s.pushToast);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [tagFilter, setTagFilter] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<SavedPhoto | null>(null);
  const [editingTags, setEditingTags] = useState<{ id: string; draft: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(id);
  }, [search]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !confirmDelete && !editingTags) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose, confirmDelete, editingTags]);

  const photosQ = useQuery({
    queryKey: qk.photos({ search: debounced, tag: tagFilter }),
    queryFn: () => listPhotos({ search: debounced || undefined, tag: tagFilter ?? undefined }),
    enabled: open,
  });

  const items = photosQ.data?.items ?? [];

  // Tag rail = union of all tags across the current page.
  const allTags = Array.from(
    new Set(items.flatMap((p) => p.tags)),
  ).sort();

  const uploadMut = useMutation({
    mutationFn: async (files: File[]) => {
      const out: SavedPhoto[] = [];
      for (const f of files) {
        if (f.size > MAX_BYTES) {
          pushToast({ type: 'error', message: `${f.name}: too large (25 MB max)` });
          continue;
        }
        try {
          const resp = await uploadPhoto(f);
          out.push(resp.photo);
        } catch (err) {
          pushToast({
            type: 'error',
            message: err instanceof StudioApiError ? `${f.name}: ${err.message}` : `${f.name}: upload failed`,
          });
        }
      }
      return out;
    },
    onSuccess: (uploaded) => {
      void qc.invalidateQueries({ queryKey: ['photos'] });
      if (uploaded.length > 0) {
        pushToast({
          type: 'success',
          message: `Uploaded ${uploaded.length} photo${uploaded.length === 1 ? '' : 's'}`,
        });
      }
    },
  });

  const tagsMut = useMutation({
    mutationFn: async (args: { id: string; tags: string[] }) =>
      patchPhoto(args.id, { tags: args.tags }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['photos'] });
      setEditingTags(null);
    },
    onError: (err) =>
      pushToast({
        type: 'error',
        message: err instanceof StudioApiError ? err.message : 'Could not save tags',
      }),
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => deletePhoto(id),
    onSuccess: () => {
      pushToast({ type: 'success', message: 'Photo deleted' });
      setConfirmDelete(null);
      void qc.invalidateQueries({ queryKey: ['photos'] });
    },
    onError: (err) =>
      pushToast({
        type: 'error',
        message: err instanceof StudioApiError ? err.message : 'Could not delete photo',
      }),
  });

  if (!open) return null;
  return (
    <>
      <div className="modal-backdrop" role="presentation" onClick={onClose}>
        <div
          className="modal modal--lg photo-library"
          role="dialog"
          aria-modal="true"
          aria-label="Photo library"
          onClick={(e) => e.stopPropagation()}
        >
          <header className="modal__header">
            <h3>{mode === 'pick' ? 'Pick a photo' : 'Photo library'}</h3>
            <button type="button" className="modal__close" onClick={onClose} aria-label="Close">
              <X size={18} strokeWidth={1.8} />
            </button>
          </header>

          <div className="modal__body photo-library__body">
            {/* Toolbar */}
            <div className="photo-library__toolbar">
              <div className="photo-library__search">
                <Search size={16} aria-hidden="true" strokeWidth={1.6} />
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by filename…"
                  aria-label="Search photos"
                />
              </div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                hidden
                onChange={(e) => {
                  const files = Array.from(e.target.files ?? []);
                  if (files.length > 0) uploadMut.mutate(files);
                  e.target.value = '';
                }}
              />
              <button
                type="button"
                className="btn btn--primary btn--sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadMut.isPending}
              >
                <Upload size={14} strokeWidth={1.6} aria-hidden="true" />
                {uploadMut.isPending ? 'Uploading…' : 'Upload photos'}
              </button>
            </div>

            {/* Tag rail */}
            {allTags.length > 0 && (
              <div className="photo-library__tags" role="tablist" aria-label="Filter by tag">
                <button
                  type="button"
                  role="tab"
                  aria-selected={tagFilter === null}
                  className={`chip${tagFilter === null ? ' is-active' : ''}`}
                  onClick={() => setTagFilter(null)}
                >
                  All
                </button>
                {allTags.map((t) => (
                  <button
                    key={t}
                    type="button"
                    role="tab"
                    aria-selected={tagFilter === t}
                    className={`chip${tagFilter === t ? ' is-active' : ''}`}
                    onClick={() => setTagFilter((cur) => (cur === t ? null : t))}
                  >
                    <TagIcon size={12} strokeWidth={1.6} aria-hidden="true" /> {t}
                  </button>
                ))}
              </div>
            )}

            {/* Grid */}
            {photosQ.isLoading ? (
              <div className="photo-library__loading">Loading photos…</div>
            ) : items.length === 0 ? (
              <div className="photo-library__empty">
                <ImageIcon size={48} strokeWidth={1.4} aria-hidden="true" />
                <p>
                  {debounced || tagFilter
                    ? 'No photos match. Try a different search or clear the tag.'
                    : 'No photos yet. Upload one to get started — JPG, PNG, WebP, or HEIC up to 25 MB.'}
                </p>
              </div>
            ) : (
              <ul className="photo-library__grid" role="list">
                {items.map((p) => {
                  const isEditing = editingTags?.id === p.id;
                  return (
                    <li key={p.id} className="photo-library__item">
                      <button
                        type="button"
                        className="photo-library__thumb"
                        onClick={() => {
                          if (mode === 'pick' && onPick) {
                            patchPhoto(p.id, { bump_last_used: true }).catch(() => {});
                            onPick(p);
                          }
                        }}
                        disabled={mode === 'manage'}
                      >
                        {p.url ? (
                          <img src={p.url} alt={p.filename} loading="lazy" />
                        ) : (
                          <div className="photo-library__thumb-empty" aria-hidden="true">
                            <ImageIcon size={28} strokeWidth={1.4} />
                          </div>
                        )}
                      </button>
                      <div className="photo-library__meta">
                        <div className="photo-library__name" title={p.filename}>
                          {p.filename}
                        </div>
                        {isEditing ? (
                          <form
                            className="photo-library__tag-edit"
                            onSubmit={(e) => {
                              e.preventDefault();
                              const tags = editingTags!.draft
                                .split(',')
                                .map((t) => t.trim().toLowerCase())
                                .filter((t) => t.length > 0 && t.length <= 40)
                                .slice(0, 20);
                              tagsMut.mutate({ id: p.id, tags });
                            }}
                          >
                            <input
                              type="text"
                              value={editingTags!.draft}
                              onChange={(e) =>
                                setEditingTags({ id: p.id, draft: e.target.value })
                              }
                              autoFocus
                              placeholder="comma, separated, tags"
                              aria-label="Edit tags"
                            />
                            <button
                              type="submit"
                              className="photo-library__icon-btn"
                              aria-label="Save tags"
                            >
                              <Check size={14} strokeWidth={2} />
                            </button>
                            <button
                              type="button"
                              className="photo-library__icon-btn"
                              aria-label="Cancel"
                              onClick={() => setEditingTags(null)}
                            >
                              <X size={14} strokeWidth={2} />
                            </button>
                          </form>
                        ) : (
                          <div className="photo-library__tags-row">
                            {p.tags.length > 0 ? (
                              p.tags.map((t) => (
                                <span key={t} className="photo-library__tag">
                                  {t}
                                </span>
                              ))
                            ) : (
                              <span className="photo-library__tag photo-library__tag--muted">
                                no tags
                              </span>
                            )}
                            {mode === 'manage' && (
                              <button
                                type="button"
                                className="photo-library__icon-btn"
                                onClick={() =>
                                  setEditingTags({ id: p.id, draft: p.tags.join(', ') })
                                }
                                aria-label={`Edit tags for ${p.filename}`}
                              >
                                <Pencil size={12} strokeWidth={1.6} />
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      {mode === 'manage' && (
                        <button
                          type="button"
                          className="photo-library__delete"
                          aria-label={`Delete ${p.filename}`}
                          onClick={() => setConfirmDelete(p)}
                        >
                          <Trash2 size={14} strokeWidth={1.6} />
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <footer className="modal__footer">
            <button type="button" className="btn btn--ghost" onClick={onClose}>
              Close
            </button>
          </footer>
        </div>
      </div>

      <ConfirmDialog
        open={!!confirmDelete}
        title={`Delete "${confirmDelete?.filename ?? ''}"?`}
        body={
          <p>
            Permanently removes this photo from your library. Designs that already use it
            won&rsquo;t be affected — they have their own embedded copy.
          </p>
        }
        confirmLabel="Delete"
        destructive
        busy={deleteMut.isPending}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => {
          if (confirmDelete) deleteMut.mutate(confirmDelete.id);
        }}
      />
    </>
  );
}
